import { useState, useEffect } from 'react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

function AtendimentoDefensoriaModal({
  isOpen,
  onClose,
  atendimento,
  movimentacaoId,
  movimentacaoFinalizada,
  onSaveSuccess,
  adolescenteNome,
  theme = 'dark',
}) {
  const { hasPermission } = useAuth();

  const canEdit =
    (hasPermission('atendimentos.create') || hasPermission('atendimentos.edit')) && !movimentacaoFinalizada;

  const canViewOnly = !canEdit && hasPermission('atendimentos.view');
  const isReadOnly = !canEdit;

  const [formData, setFormData] = useState({
    observacao: '',
    nome_representante: '',
    check_entrada: false,
    check_saida: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (atendimento) {
      setFormData({
        observacao: atendimento.observacao || '',
        nome_representante: atendimento.nome_representante || '',
        check_entrada: !!atendimento.check_entrada,
        check_saida: !!atendimento.check_saida,
      });
    } else if (isOpen) {
      setFormData({
        observacao: '',
        nome_representante: '',
        check_entrada: false,
        check_saida: false,
      });
    }
  }, [atendimento, isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    const val = type === 'checkbox' ? checked : value;

    setFormData((prev) => {
      const next = { ...prev, [name]: val };
      if (name === 'check_entrada' && !val) next.check_saida = false; // cascata
      return next;
    });

    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) {
      onClose();
      return;
    }

    setSaving(true);
    setError(null);
    setValidationErrors({});

    try {
      if (atendimento) {
        await api.put(`/atendimentos-defensoria/${atendimento.id}`, formData);
      } else {
        await api.post(`/movimentacoes/${movimentacaoId}/atendimentos-defensoria`, formData);
      }
      onSaveSuccess && onSaveSuccess('Atendimento da Defensoria salvo com sucesso!');
      onClose();
    } catch (err) {
      const msg =
        err.userMessage ||
        err.response?.data?.message ||
        'Ocorreu um erro ao salvar o atendimento.';
      setError(msg);
      if (err.validationErrors) setValidationErrors(err.validationErrors);
      if (!err.silence) console.error('Erro ao salvar atendimento da Defensoria:', err);
    } finally {
      setSaving(false);
    }
  };

  const disabledFields = isReadOnly || saving;

  return (
    <div
      className={`modal fade show dashboard-themed-modal dashboard-themed-modal--${theme}`}
      style={{ display: 'block' }}
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
    >
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content bg-dark text-light dashboard-themed-modal-content">
          <div className="modal-header">
            <div className="w-100">
              <h5 className="modal-title atendimento-modal-title mb-1">Atendimento Defensoria/Advogado</h5>
            </div>
            <button
              type="button"
              className={theme === 'light' ? 'btn-close' : 'btn-close btn-close-white'}
              onClick={onClose}
              disabled={saving}
              aria-label="Fechar"
            ></button>
          </div>

          {/* Linha + nome do adolescente */}
          <div className="px-4 pt-2">
            <hr className="text-secondary my-2" />
            <div className="mb-3">
              <span className="text-secondary me-1">Adolescente:</span>
              <span className="text-light fw-semibold">
                {adolescenteNome || '—'}
              </span>
            </div>
          </div>

          <div className="modal-body pt-0">
            <form onSubmit={handleSubmit} noValidate>
              {error && <div className="alert alert-danger">{error}</div>}

              {canViewOnly ? (
                // MODO SOMENTE LEITURA (visual)
                <div className="mb-2">
                  <div className="mb-3">
                    <small className="text-secondary d-block">Nome do Representante</small>
                    <span className="text-light">
                      {formData.nome_representante?.trim() || '—'}
                    </span>
                  </div>

                  <div className="mb-3">
                    <small className="text-secondary d-block">Observação</small>
                    <span className="text-light">
                      {formData.observacao?.trim() || '—'}
                    </span>
                  </div>

                  <div className="mb-2">
                    <small className="text-secondary d-block">Status</small>
                    <span className="text-light">
                      {formData.check_entrada
                        ? formData.check_saida
                          ? 'Finalizado'
                          : 'Em andamento'
                        : 'Não iniciado'}
                    </span>
                  </div>
                </div>
              ) : (
                <>
                  <div className="mb-3">
                    <label htmlFor="nome_representante" className="form-label">
                      Defensor ou Advogado Particular:
                    </label>
                    <input
                      type="text"
                      className={`form-control ${
                        validationErrors.nome_representante ? 'is-invalid' : ''
                      }`}
                      id="nome_representante"
                      name="nome_representante"
                      value={formData.nome_representante}
                      onChange={handleChange}
                      placeholder="Digite o nome"
                      maxLength={250}
                      disabled={disabledFields}
                    />
                    {validationErrors.nome_representante && (
                      <div className="invalid-feedback">
                        {validationErrors.nome_representante.join(', ')}
                      </div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label
                      htmlFor="observacao_defensoria"
                      className="form-label"
                    >
                      Observação:
                    </label>
                    <textarea
                      className={`form-control ${
                        validationErrors.observacao ? 'is-invalid' : ''
                      }`}
                      id="observacao_defensoria"
                      name="observacao"
                      rows="3"
                      value={formData.observacao}
                      onChange={handleChange}
                      disabled={disabledFields}
                    ></textarea>
                    {validationErrors.observacao && (
                      <div className="invalid-feedback">
                        {validationErrors.observacao.join(', ')}
                      </div>
                    )}
                  </div>

                  <div className="form-check form-switch mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="check_entrada_defensoria"
                      name="check_entrada"
                      checked={formData.check_entrada}
                      onChange={handleChange}
                      disabled={disabledFields}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="check_entrada_defensoria"
                    >
                      Atendimento Iniciado
                    </label>
                  </div>

                  <div className="form-check form-switch mb-1">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="check_saida_defensoria"
                      name="check_saida"
                      checked={formData.check_saida}
                      onChange={handleChange}
                      disabled={disabledFields || !formData.check_entrada}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="check_saida_defensoria"
                    >
                      Atendimento Finalizado
                    </label>
                  </div>

                  {!canViewOnly && (
                    <small className="text-secondary">
                      Para finalizar, o atendimento deve estar &quot;Iniciado&quot;.
                    </small>
                  )}
                </>
              )}
            </form>
          </div>

          <div className="modal-footer">
            {canViewOnly ? (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                Fechar
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AtendimentoDefensoriaModal;
